import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { changePassword, setAuthToken } from "@/lib/dentiumApi";
import { SettingsCard, SaveButton } from "@/components/settings/SettingsCard";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

const PasswordField = ({ id, label, value, onChange, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 block">{label}</Label>
      <div className="relative">
        <Input id={id} data-testid={`${id}-input`} type={show ? "text" : "password"} value={value} autoComplete={autoComplete}
          maxLength={128} onChange={(e) => onChange(e.target.value)} className="pr-10 h-11" />
        <button type="button" data-testid={`${id}-toggle`} onClick={() => setShow((v) => !v)}
          title={show ? "Ocultar contraseña" : "Mostrar contraseña"} aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center text-[#8A7E70] hover:bg-[#F1EBE1] transition-colors">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const validate = (f) => {
  if (!f.current_password) return "Introduce tu contraseña actual";
  if (f.new_password.length < 8) return "La nueva contraseña debe tener al menos 8 caracteres";
  if (f.new_password !== f.confirm_password) return "La confirmación no coincide con la nueva contraseña";
  if (f.new_password === f.current_password) return "La nueva contraseña debe ser distinta de la actual";
  return "";
};

export const ChangePasswordPanel = () => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const set = (k) => (v) => { setForm((s) => ({ ...s, [k]: v })); setError(""); };

  const save = useMutation({
    mutationFn: () => changePassword(form),
    onSuccess: (d) => {
      setAuthToken(d.access_token);
      setForm(EMPTY);
      toast.success("Contraseña actualizada", { description: "Se han cerrado las sesiones abiertas en otros dispositivos." });
    },
    onError: (e) => setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo cambiar la contraseña"),
  });

  const submit = (e) => {
    e.preventDefault();
    const msg = validate(form);
    if (msg) return setError(msg);
    save.mutate();
  };

  return (
    <SettingsCard icon={KeyRound} title="Cambiar contraseña" testid="change-password-panel"
      description="Al cambiarla se cerrará la sesión en el resto de dispositivos. Mínimo 8 caracteres.">
      <form onSubmit={submit} className="space-y-4">
        <PasswordField id="current-password" label="Contraseña actual" value={form.current_password} onChange={set("current_password")} autoComplete="current-password" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PasswordField id="new-password" label="Nueva contraseña" value={form.new_password} onChange={set("new_password")} autoComplete="new-password" />
          <PasswordField id="confirm-password" label="Confirmar nueva contraseña" value={form.confirm_password} onChange={set("confirm_password")} autoComplete="new-password" />
        </div>
        {error && <div data-testid="change-password-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
        <div className="flex justify-end">
          <SaveButton testid="change-password-submit" pending={save.isPending} onClick={undefined} />
        </div>
      </form>
    </SettingsCard>
  );
};
